import { type CallhomeCamera } from '@solinkcorp/api-types';
import {
  type SdmLivePlayerErrorCode,
  type SdmLivePlayerState,
} from '../../../contexts/LiveVideoContext';

/**
 * Why a live feed can't be shown:
 * - `missingZoneTags`: the widget's tags aren't assigned to any zone or line, so no camera matches.
 * - `cameraOffline`: the camera itself is offline.
 * - `unavailable`: the stream failed for any other reason, including no player being available.
 */
export type LiveFeedError = 'missingZoneTags' | 'cameraOffline' | 'unavailable';

/**
 * Decide which overlay, if any, a live feed should show.
 *
 * Ordered most specific first: a configuration problem is worth reporting even if the stream
 * would also have failed, and an offline camera explains a failure better than a generic one.
 *
 * Nothing is reported until the camera lookup has loaded: an empty match list means "no zone
 * tags" only once the config is in, so a slow fetch cannot show that error on a working feed.
 *
 * @param camerasLoaded Whether the site and vision analytics config behind the camera match
 *   have arrived.
 * @param activeCamera The camera currently on screen, or undefined when none matched the tags.
 * @param canPlay Whether a player and the site data needed to stream are available.
 * @param playerState The active camera's last reported connection state.
 * @returns The error to display, or null when the feed should play (or is still loading).
 */
export function selectLiveFeedError({
  camerasLoaded,
  activeCamera,
  canPlay,
  playerState,
}: {
  camerasLoaded: boolean;
  activeCamera: CallhomeCamera | undefined;
  canPlay: boolean;
  playerState: SdmLivePlayerState | undefined;
}): LiveFeedError | null {
  if (!camerasLoaded) return null;
  if (!activeCamera) return 'missingZoneTags';
  if (activeCamera.status !== 'online') return 'cameraOffline';
  if (!canPlay || playerState === 'error') return 'unavailable';
  return null;
}

/**
 * The error code reported in telemetry. It splits `unavailable` into its causes: no player or
 * site data (`playerUnavailable`), or the reason the player gave for its failure.
 */
export type LiveFeedErrorCode =
  Exclude<LiveFeedError, 'unavailable'> | 'playerUnavailable' | SdmLivePlayerErrorCode;

/**
 * Classify a displayed error for telemetry.
 *
 * @param error The error the widget displays, from `selectLiveFeedError`.
 * @param canPlay Whether a player and the site data needed to stream are available.
 * @param playerErrorCode The reason the active camera's player gave for its error, if any.
 * @returns The error code, or null when the feed shows no error.
 */
export function toLiveFeedErrorCode({
  error,
  canPlay,
  playerErrorCode,
}: {
  error: LiveFeedError | null;
  canPlay: boolean;
  playerErrorCode: SdmLivePlayerErrorCode | undefined;
}): LiveFeedErrorCode | null {
  if (error !== 'unavailable') return error;
  if (!canPlay) return 'playerUnavailable';
  return playerErrorCode ?? 'playbackFailed';
}
